import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Computer as ComputerIcon,
  DeveloperMode as DeveloperModeIcon,
  BugReport as BugReportIcon,
} from '@mui/icons-material';

interface IEnvironmentBadgeProps {
  environment?: string;
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

const EnvironmentBadge: React.FC<IEnvironmentBadgeProps> = ({
  environment,
  position = 'top-right',
}) => {
  // Get environment from props or environment config
  const env = environment || import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE;
  
  // Don't show badge in production
  if (env === 'production') {
    return null;
  }

  // Environment configuration
  const getEnvironmentConfig = (envName: string) => {
    switch (envName.toLowerCase()) {
      case 'localhost':
      case 'local':
        return {
          label: 'LOCAL',
          color: '#4CAF50', // Green
          bgColor: '#E8F5E8',
          icon: <ComputerIcon sx={{ fontSize: 14 }} />,
          description: 'Local Development'
        };
      case 'development':
      case 'dev':
        return {
          label: 'DEV',
          color: '#FF9800', // Orange
          bgColor: '#FFF3E0',
          icon: <DeveloperModeIcon sx={{ fontSize: 14 }} />,
          description: 'Development Server'
        };
      case 'staging':
      case 'stage':
        return {
          label: 'STAGING',
          color: '#2196F3', // Blue
          bgColor: '#E3F2FD',
          icon: <BugReportIcon sx={{ fontSize: 14 }} />,
          description: 'Staging Environment'
        };
      default:
        return {
          label: env.toUpperCase(),
          color: '#9C27B0', // Purple
          bgColor: '#F3E5F5',
          icon: <DeveloperModeIcon sx={{ fontSize: 14 }} />,
          description: 'Development Environment'
        };
    }
  };

  const config = getEnvironmentConfig(env);

  // Position styles
  const getPositionStyles = () => {
    const baseStyles = {
      position: 'fixed' as const,
      zIndex: 9999,
      pointerEvents: 'none' as const,
    };

    switch (position) {
      case 'top-left':
        return { ...baseStyles, top: 16, left: 16 };
      case 'top-right':
        return { ...baseStyles, top: 16, right: 16 };
      case 'bottom-left':
        return { ...baseStyles, bottom: 16, left: 16 };
      case 'bottom-right':
        return { ...baseStyles, bottom: 16, right: 16 };
      default:
        return { ...baseStyles, top: 16, right: 16 };
    }
  };

  return (
    <Box sx={getPositionStyles()}>
      <Chip
        icon={config.icon}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 'bold',
                fontSize: '0.65rem',
                letterSpacing: '0.5px',
              }}
            >
              {config.label}
            </Typography>
          </Box>
        }
        size="small"
        sx={{
          backgroundColor: config.bgColor,
          color: config.color,
          border: `1px solid ${alpha(config.color, 0.3)}`,
          fontWeight: 'bold',
          fontSize: '0.65rem',
          height: 24,
          borderRadius: '12px',
          boxShadow: `0 2px 8px ${alpha(config.color, 0.2)}`,
          '& .MuiChip-icon': {
            color: config.color,
            fontSize: 14,
          },
          '& .MuiChip-label': {
            padding: '0 6px',
          },
          // Hover effect for debugging
          '&:hover': {
            backgroundColor: alpha(config.color, 0.1),
            transform: 'scale(1.05)',
            transition: 'all 0.2s ease-in-out',
            pointerEvents: 'auto',
            cursor: 'help',
          },
          // Animation
          animation: 'fadeInDown 0.5s ease-out',
          '@keyframes fadeInDown': {
            '0%': {
              opacity: 0,
              transform: 'translateY(-20px)',
            },
            '100%': {
              opacity: 1,
              transform: 'translateY(0)',
            },
          },
        }}
        title={`${config.description} - ${env}`}
      />
    </Box>
  );
};

export default EnvironmentBadge;
